/**
 * Which region a dashboard page is looking at.
 *
 * The scope is the QUERY STRING — `?region=<slug>` — as it is on Venues, so it
 * survives a reload, is linkable, and drives one page rather than picking
 * which of two page copies to mount. Sales, Teachers, Supplies and Corp Events
 * used to be an admin copy and a regional copy of the same bucket under two
 * route trees; they are one page each now, and this is what tells them which
 * endpoint to call.
 *
 * No slug is national scope, which is what the `/dashboards/admin/…`
 * endpoints answer. A slug is regional scope, which `/dashboards/regional/
 * :slug/…` answers. Neither number changes — the page picks the request the
 * old page made.
 *
 * NATIONAL IS NOT EVERYONE'S DEFAULT. The national endpoints are gated on the
 * `read:all` Admin sentinel, so a Regional Director landing there gets a 403
 * on all four pages — which is what the retired `/r/<slug>/…` tree used to
 * spare them. A viewer without the sentinel therefore falls back to their own
 * region rather than to national, and the picker does not offer them a scope
 * they cannot read.
 */
import { useSearchParams } from 'react-router-dom';

import { usePermissions } from '../../../hooks/usePermissions';
import { Permission } from '../../../types/permissions';
import { flattenRegions, useRegionHierarchy } from '../primitives/scope/region-hierarchy';
import { regionSlugFromParam } from '../primitives/scope/region-param';
import type { ScopeRegion } from '../primitives/scope/scope.types';
import { useOwnRegions } from '../primitives/scope/use-own-regions';

export interface DashboardScope {
  /** The region to report on, or null for national scope. */
  regionSlug: string | null;
  /** Its id, for the picker's current-row highlight. Null at national scope,
   *  and until the hierarchy has loaded — the picker reads both as "no row is
   *  current", which is the right answer while there is nothing to mark. */
  currentRegionId: string | null;
  /** What the current scope is called, for the header. */
  label: string;
  /** The hierarchy, flat, for the picker. */
  regions: ScopeRegion[];
  /** Whether this viewer may read the national rollup at all. False hides the
   *  all-regions row, which would otherwise offer a 403. */
  canReadNational: boolean;
  /**
   * The scope is not settled yet.
   *
   * Only ever true for a viewer who needs their own region resolved out of the
   * hierarchy and has not received it. Rendering the body meanwhile would fire
   * the national request they cannot make, so the page waits instead.
   */
  pending: boolean;
}

const NATIONAL_LABEL = 'All regions';

/**
 * These pages report on ONE region, so unlike Venues (which takes the union of a
 * viewer's regions) they use the shallowest of them. "My region" and the wait
 * for the user and permissions come from `useOwnRegions`, and `?region=all` is
 * the explicit national choice from `regionSlugFromParam`: it is never a slug,
 * so it can never reach `/dashboards/regional/all`.
 */
export function useDashboardScope(): DashboardScope {
  const [params] = useSearchParams();
  const { hasPermission } = usePermissions();
  const { pending: ownPending, own } = useOwnRegions();
  const { data } = useRegionHierarchy();

  const canReadNational = hasPermission(Permission.READ_ALL);
  const regions = data ? flattenRegions(data) : [];
  const fromUrl = params.get('region');

  // An address, `region=all` included, carries its own answer.
  const regionSlug =
    fromUrl === null ? (own[0]?.slug ?? null) : (regionSlugFromParam(fromUrl) ?? null);
  const current = regionSlug ? regions.find((r) => r.slug === regionSlug) : undefined;

  return {
    regionSlug,
    currentRegionId: current?.id ?? null,
    // The slug while the hierarchy loads, rather than the national label: it
    // is what the address says, and flashing "All regions" over a regional
    // page reads as the scope having been dropped.
    label: regionSlug ? (current?.name ?? regionSlug) : NATIONAL_LABEL,
    regions,
    canReadNational,
    // Nothing is chosen, and so nothing is fetched, until the user, their
    // permissions and (for someone who needs it) the region list are known.
    // Once they are and the viewer still has no region, the wait ends: that
    // account holds none, a misconfiguration the page should surface.
    pending: fromUrl === null && ownPending,
  };
}
